import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import { getAPIURL, setCachedBusiness, getCachedBusiness } from "../lib/utils";
import { withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";
import UserContext from "./user-context";
import { prefix } from "../lib/auth";

const BusinessContext = createContext({
  businessList: null,
  activeBusiness: null,
  fetchBusinessList: () => { },
  setActiveBusiness: () => { },
  fetchStaffList: () => { },
  staffList: null,
  setStaffList: () => { },
  teammates: null,
  activeStaffs: null,
  pendingStaffs: null,
  setNotificationContent: () => { },
  notificationContent: {
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  },
  userHasAdminAccess: false,
  currentUser: null,
  isGuestUser: false,
  renameBusiness: () => { },
  isOwner: false,
});

export const BusinessProvider = (props) => {
  const [businessList, setBusinessList] = useState(null);
  const [activeBusiness, setBusiness] = useState(null);
  const [staffList, setStaffList] = useState(null);
  const { user } = useContext(UserContext);
  const [notificationContent, setNotificationContent] = useState({
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  });
  const router = useRouter();
  const currentUser = staffList ? staffList.find(staff => staff.user.email === user?.email) : null;
  const isGuestUser = currentUser?.role === 'guest';
  const isOwner = currentUser?.role === 'owner';
  const activeStaffs = staffList ? staffList.filter(staff => staff.user.email !== '' && staff.role !== 'owner') : null;
  const pendingStaffs = staffList ? staffList.filter(staff => staff.user.email === '') : null;
  const userHasAdminAccess = staffList
    ? staffList.some(
      staff =>
        staff.user.email === user?.email &&
        (staff.role === "admin" || staff.role === "owner")
    )
    : false;

  const fetchBusinessList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL("businesses/"), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            if (json.length === 0) {
              router.push("/businesses/new");
              return;
          }

            setBusinessList(json);

            const businessUuid = router.query.uuid;

            if (businessUuid) {
              const activeBusiness = json.find(
                (business) => business.uuid == businessUuid
              );

              if (activeBusiness) {
                setActiveBusiness(activeBusiness);
                return;
              }
            }

            const cachedBusiness = getCachedBusiness();

            const activeBusiness = cachedBusiness
              ? json.find((business) => business.uuid == cachedBusiness) ||
              json[0]
              : json[0];
            setActiveBusiness(activeBusiness);
          });
        }
        return res.json()
          .catch(() => ({ status: res.status }))
          .then((json) => Promise.reject(json));
      }).catch((error) => {
        // The app renders <Loader /> until `activeBusiness` is set, so a failure
        // here is an indefinite spinner. Say why rather than failing silently.
        console.error('Could not load the business list; the app cannot start:', error);
      });
    }, router);
  }, [router]);

  const fetchStaffList = useCallback((callBack) => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/staff/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            callBack && callBack(json);
            setStaffList(json);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }
  );

  const renameBusiness = (businessUuid, newName, callBack) => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${businessUuid}/`), {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: newName }),
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            callBack && callBack(json);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  };

  const fetchInstalledPegs = () => {

    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/pegs/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            const names = json.map(peg => peg.peg.name);
            
            localStorage.setItem(`${getCachedBusiness()}_installed_pegs`, JSON.stringify(names));
          });
        }
        return res.json().then((json) => {
          setPegCheckReady(true);
          return Promise.reject(json);
        });
      });
    }, router);
  };


  useEffect(() => {
    if (businessList == null && router.isReady) {
      fetchBusinessList();
    }
    if (activeBusiness) {
      fetchStaffList();
      fetchInstalledPegs()
    }
  }, [businessList, fetchBusinessList, router.isReady, activeBusiness]);

  function setActiveBusiness(business) {
    setBusiness(business);
    setCachedBusiness(business.uuid);
  }
  // Add the updateBusinessList function
  function updateBusinessList(newList) {
    setBusinessList(newList); // Update the business list state
  }
  const teammates = staffList ? staffList.filter(staff => staff.user.first_name) : null;

  const context = {
    businessList,
    activeBusiness,
    fetchBusinessList,
    setActiveBusiness,
    updateBusinessList,
    fetchStaffList,
    staffList,
    setStaffList,
    teammates,
    activeStaffs,
    pendingStaffs,
    notificationContent,
    setNotificationContent,
    userHasAdminAccess,
    currentUser,
    isGuestUser,
    renameBusiness,
    isOwner,
  };

  return (
    <BusinessContext.Provider value={context}>
      {props.children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext;
